import React, { useState } from 'react';
import { Tag, Percent, Euro, Check, Wifi, Wrench, CalendarClock, CreditCard, Layers } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  CATEGORY_LABELS, CATEGORY_STYLES, PRODUCT_STATUS_CONFIG,
  formatPrice, formatDateIt, getEffectivePrice,
} from './storeConstants';

// Per immagini servite dal backend (path relativi /api/store/product-image/...) servire l'URL completo
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Detecta prodotti "Monoblocco": il prodotto coincide con una sola unità (es. PDC monoblocco).
// Per questi mostriamo una sola foto (image_url_full o image_url) anche se l'Excel ha popolato
// le altre colonne foto: sono varianti visive della stessa unità → niente gallery.
const isMonoblock = (product) => {
  const fields = [product.product_code, product.model, product.product_line, product.description]
    .filter(Boolean).map((s) => s.toLowerCase());
  return fields.some((s) => /(^|[^a-z])mono(\b|bloc|block)/.test(s));
};

/**
 * Hero immagine prodotto con supporto a galleria multi-immagine
 * (per prodotti compositi come PDC split / climatizzatori multi-split).
 *
 * Comportamento:
 *  - Monoblocco / unità singola: una sola immagine, no gallery
 *  - Prodotto composito: immagine hero + thumbnail strip cliccabili
 */
const ProductImageHero = ({ product, fallbackIcon: FallbackIcon }) => {
  // Per i Monoblocco usiamo solo l'immagine principale: foto_prodotto_completo o image_url
  const mono = isMonoblock(product);
  const rawImages = mono
    ? [
        (product.image_url_full || product.image_url) && {
          src: resolveImageUrl(product.image_url_full || product.image_url),
          label: 'Unità', key: 'unit',
        },
      ].filter(Boolean)
    : [
        product.image_url_full && { src: resolveImageUrl(product.image_url_full), label: 'Sistema completo', key: 'full' },
        product.image_url && { src: resolveImageUrl(product.image_url), label: 'Unità interna', key: 'main' },
        product.image_url_external_unit && { src: resolveImageUrl(product.image_url_external_unit), label: 'Unità esterna', key: 'external' },
      ].filter(Boolean);

  // Dedup per src identico (caso comune se backend ha già deduplicato file)
  const seenSrc = new Set();
  const allImages = rawImages.filter((img) => {
    if (seenSrc.has(img.src)) return false;
    seenSrc.add(img.src);
    return true;
  });

  const [activeIdx, setActiveIdx] = useState(0);
  const hasGallery = allImages.length >= 2;
  const activeImage = allImages[activeIdx];

  return (
    <div className="relative">
      <div className="h-36 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center overflow-hidden">
        {activeImage ? (
          <img
            src={activeImage.src}
            alt={`${product.model || product.product_code} - ${activeImage.label}`}
            className="h-full w-full object-contain p-2"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <FallbackIcon className="h-12 w-12 text-slate-300" />
        )}
        <Badge variant="outline" className="absolute top-2 right-2 text-[10px] bg-white/95 shadow-sm">
          {product.product_code}
        </Badge>
      </div>
      {hasGallery && (
        <div className="flex gap-1 px-2 py-1.5 bg-slate-50 border-t border-slate-200" data-testid="product-image-gallery">
          {allImages.map((img, i) => (
            <button
              key={img.key}
              type="button"
              onClick={(e) => { e.stopPropagation(); setActiveIdx(i); }}
              className={`flex-1 h-10 rounded border bg-white overflow-hidden transition-all ${
                i === activeIdx
                  ? 'border-cyan-500 ring-1 ring-cyan-300'
                  : 'border-slate-200 hover:border-slate-300 opacity-70 hover:opacity-100'
              }`}
              title={img.label}
              data-testid={`product-image-thumb-${img.key}`}
            >
              <img src={img.src} alt={img.label} className="h-full w-full object-contain p-0.5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Card prodotto per agenti store (e admin in vista card).
 * Mostra: immagine, marca, linea, modello, categoria, potenza/capacità,
 * prezzo cliente, promo attiva, validità, badge (tasso zero, voucher, installazione, FIDO).
 */
export const ProductCardAgent = ({
  product,
  onSelect,
  isSelected = false,
  buttonLabel = 'Seleziona per preventivo',
  showButton = true,
  showStatus = false,
}) => {
  const style = CATEGORY_STYLES[product.category] || { icon: Tag, color: 'bg-gray-100 text-gray-700' };
  const CategoryIcon = style.icon;
  const specs = product.technical_specs || {};
  const effectivePrice = getEffectivePrice(product);
  const hasDiscount = effectivePrice && product.base_price && effectivePrice < product.base_price;
  const hasVoucher = product.voucher_available || (product.voucher_amount || 0) > 0;
  const hasZonal = (product.zonal_discount || 0) > 0;
  const statusConfig = PRODUCT_STATUS_CONFIG[product.effective_status || product.status];
  const powerOrCapacity = product.power_label || product.capacity_label
    || (specs.potenza_kw ? `${specs.potenza_kw} kW` : null)
    || (specs.capacita_litri ? `${specs.capacita_litri} L` : null);

  return (
    <div
      className={`relative flex flex-col rounded-xl border-2 overflow-hidden bg-white transition-all duration-200 ${
        isSelected
          ? 'border-blue-500 ring-2 ring-blue-200 shadow-lg'
          : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
      } ${onSelect ? 'cursor-pointer' : ''}`}
      onClick={onSelect}
      data-testid={`product-card-${product.product_code}`}
    >
      {/* Immagine prodotto — supporta gallery per prodotti con unità multiple */}
      <ProductImageHero product={product} fallbackIcon={CategoryIcon} />
      {showStatus && statusConfig && (
        <Badge className="absolute top-3 left-3 z-10 text-xs border bg-white/95 shadow-sm">
          {statusConfig.label}
        </Badge>
      )}

      <div className="flex-1 flex flex-col p-4">
        {/* Marca + linea + categoria */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {product.brand}{product.product_line ? ` ${product.product_line}` : ''}
            </p>
            <h4 className="font-bold text-base leading-tight mt-0.5">{product.model}</h4>
          </div>
          <div className={`p-1.5 rounded-lg shrink-0 ${style.color}`}>
            <CategoryIcon className="h-4 w-4" />
          </div>
        </div>

        {/* Specifiche */}
        <div className="flex flex-wrap gap-1 mt-2">
          <Badge variant="secondary" className="text-xs py-0">
            {CATEGORY_LABELS[product.category] || product.category}
          </Badge>
          {powerOrCapacity && (
            <Badge variant="secondary" className="text-xs py-0">{powerOrCapacity}</Badge>
          )}
          {specs.classe_energetica && (
            <Badge variant="secondary" className="text-xs py-0 bg-green-100 text-green-700">
              {specs.classe_energetica}
            </Badge>
          )}
          {specs.wifi && (
            <Badge variant="secondary" className="text-xs py-0 bg-purple-100 text-purple-700">
              <Wifi className="h-3 w-3" />
            </Badge>
          )}
        </div>

        {/* Prezzo */}
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-slate-900" data-testid={`price-${product.product_code}`}>
            {formatPrice(effectivePrice)}
          </span>
          {hasDiscount && (
            <span className="text-sm text-muted-foreground line-through">
              {formatPrice(product.base_price)}
            </span>
          )}
          <span className="text-xs text-muted-foreground">IVA incl.</span>
        </div>

        {/* Promo e badge */}
        <div className="mt-2 space-y-1 flex-1">
          {hasVoucher && (
            <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
              <Tag className="h-3 w-3 shrink-0" />
              <span>Voucher {product.voucher_amount > 0 ? formatPrice(product.voucher_amount) : 'disponibile'}</span>
            </div>
          )}
          {hasZonal && (
            <div className="flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded">
              <Percent className="h-3 w-3 shrink-0" />
              <span>Sconto Zonale {formatPrice(product.zonal_discount)}</span>
            </div>
          )}
          {product.tasso_zero_available && (
            <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
              <Euro className="h-3 w-3 shrink-0" />
              <span>Tasso Zero {product.financing_months || 24} mesi</span>
            </div>
          )}
          {product.fido_in_bolletta && (
            <div className="flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded">
              <CreditCard className="h-3 w-3 shrink-0" />
              <span>FIDO in bolletta {product.fido_months || 48} mesi</span>
            </div>
          )}
          {product.installation_included && (
            <div className="flex items-center gap-1 text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded">
              <Wrench className="h-3 w-3 shrink-0" />
              <span>Installazione inclusa</span>
            </div>
          )}
          {product.promo_valid_to && (
            <div className="flex items-center gap-1 text-xs text-rose-600 px-2 py-0.5">
              <CalendarClock className="h-3 w-3 shrink-0" />
              <span>Promo fino al {formatDateIt(product.promo_valid_to)}</span>
            </div>
          )}
        </div>

        {showButton && (
          <Button
            className="w-full mt-3"
            variant={isSelected ? 'default' : 'outline'}
            data-testid={`select-product-${product.product_code}`}
          >
            {isSelected ? <Check className="h-4 w-4 mr-2" /> : null}
            {buttonLabel}
          </Button>
        )}
      </div>

      {isSelected && (
        <div className="absolute top-2 left-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center z-10">
          <Check className="h-4 w-4 text-white" />
        </div>
      )}
    </div>
  );
};
